import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Paper,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
} from '@mui/material';
import { Visibility as PreviewIcon } from '@mui/icons-material';
import { ExercisePattern, PatternContainer } from '../types';
import patternService from '../services/pattern.service';
import patternContainerService from '../services/patternContainer.service';
import PatternPdfViewer from './PatternPdfViewer';
import PdfDisplay from './PdfDisplay';
import LatexEditor from './LatexEditor';
import PythonEditor from './PythonEditor';

interface PatternViewImportDialogProps {
  open: boolean;
  onClose: () => void;
  pattern: ExercisePattern | null;
  onImportSuccess?: () => void;
}

const PatternViewImportDialog: React.FC<PatternViewImportDialogProps> = ({
  open,
  onClose,
  pattern,
  onImportSuccess,
}) => {
  const [detailedPattern, setDetailedPattern] = useState<ExercisePattern | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewKey, setPdfPreviewKey] = useState(0);

  // Import options
  const [selectedContainer, setSelectedContainer] = useState<number | null>(null);
  const [copySolutions, setCopySolutions] = useState(true);
  const [copySkills, setCopySkills] = useState(true);
  const [containers, setContainers] = useState<PatternContainer[]>([]);
  const [showImportOptions, setShowImportOptions] = useState(false);

  // Load detailed pattern data
  useEffect(() => {
    if (pattern && open) {
      setLoading(true);
      setError(null);
      setShowPdfPreview(false);
      patternService.getPattern(pattern.id)
        .then(data => {
          setDetailedPattern(data);
        })
        .catch(err => {
          console.error('Error loading pattern details:', err);
          setError('Erreur lors du chargement des détails du pattern');
        })
        .finally(() => setLoading(false));
    }
  }, [pattern, open]);

  // Load containers for selection
  useEffect(() => {
    if (open) {
      patternContainerService.getAllContainers()
        .then(data => {
          setContainers(data);
          if (data.length > 0 && selectedContainer === null) {
            setSelectedContainer(data[0].id);
          }
        })
        .catch(err => {
          console.error('Error loading containers:', err);
        });
    }
  }, [open, selectedContainer]);

  const handlePreview = () => {
    setShowPdfPreview(true);
    setPdfPreviewKey(prev => prev + 1);
  };

  const handleImport = async () => {
    if (!detailedPattern) return;

    setImporting(true);
    setError(null);

    try {
      await patternService.importPattern(detailedPattern.id, {
        container: selectedContainer,
        copy_solutions: copySolutions,
        copy_skills: copySkills,
      });

      if (onImportSuccess) {
        onImportSuccess();
      }
      onClose();
    } catch (err: any) {
      console.error('Error importing pattern:', err);
      setError(err.response?.data?.error || 'Erreur lors de l\'importation du pattern');
    } finally {
      setImporting(false);
    }
  };

  const handleClose = () => {
    setShowPdfPreview(false);
    setDetailedPattern(null);
    setError(null);
    setShowImportOptions(false);
    onClose();
  };

  if (!pattern) return null;

  const latexTemplate = detailedPattern?.latex_template || pattern.latex_template;
  const pythonGenerator = detailedPattern?.python_generator || pattern.python_generator;
  const isReusable = detailedPattern?.is_reusable !== undefined ? detailedPattern.is_reusable : pattern.is_reusable;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          height: '90vh',
          width: '95vw',
          maxWidth: '95vw'
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h6">{pattern.title}</Typography>
            {pattern.created_by_name && (
              <Typography variant="body2" color="text.secondary">
                Par {pattern.created_by_name}
              </Typography>
            )}
          </Box>
          <Button
            variant="outlined"
            onClick={() => setShowImportOptions(!showImportOptions)}
          >
            {showImportOptions ? 'Masquer les options d\'import' : 'Importer ce pattern'}
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Import Options Section */}
            <Collapse in={showImportOptions}>
              <Paper sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
                <Typography variant="h6" gutterBottom>
                  Options d'importation
                </Typography>

                <Divider sx={{ my: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Container de destination</InputLabel>
                      <Select
                        value={selectedContainer || ''}
                        onChange={(e) => setSelectedContainer(e.target.value as number)}
                        label="Container de destination"
                      >
                        {containers.map((container) => (
                          <MenuItem key={container.id} value={container.id}>
                            {container.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={copySolutions}
                        onChange={(e) => {
                          setCopySolutions(e.target.checked);
                          if (!e.target.checked) {
                            setCopySkills(false);
                          }
                        }}
                      />
                    }
                    label="Copier les solutions associées (vers le container 'Solutions importées')"
                  />

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={copySkills}
                        onChange={(e) => setCopySkills(e.target.checked)}
                        disabled={!copySolutions}
                      />
                    }
                    label="Copier les savoir-faire associés aux solutions (vers le container 'Savoir-faire importés')"
                  />
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  {!copySolutions && !copySkills && (
                    "Seul le pattern sera copié, sans ses solutions ni savoir-faire."
                  )}
                  {copySolutions && !copySkills && (
                    "Le pattern et ses solutions seront copiés, mais les solutions n'auront pas de savoir-faire associés."
                  )}
                  {copySolutions && copySkills && (
                    <>Les savoir-faire que vous possédez déjà ne seront pas dupliqués. Les containers "Solutions importées" et "Savoir-faire importés" seront créés automatiquement s'ils n'existent pas.</>
                  )}
                </Alert>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    onClick={handleImport}
                    variant="contained"
                    disabled={importing || !detailedPattern}
                  >
                    {importing ? <CircularProgress size={24} /> : 'Confirmer l\'importation'}
                  </Button>
                </Box>
              </Paper>
            </Collapse>

            {/* Pattern Details - Same layout as Workshop */}
            <Grid container spacing={2}>
              {/* Template LaTeX */}
              <Grid item xs={12} md={6}>
                <Box mb={1}>
                  <Typography variant="subtitle2">Template LaTeX</Typography>
                </Box>
                <LatexEditor
                  value={latexTemplate}
                  onChange={() => {}} // Read-only
                  helperText="Lecture seule - Utilisez {{variable}} pour les valeurs dynamiques"
                  height={600}
                  disabled={true}
                />
              </Grid>

              {/* PDF Preview */}
              <Grid item xs={12} md={6}>
                <Box sx={{ height: '600px' }}>
                  <Box display="flex" justifyContent="flex-end" mb={1}>
                    <Button
                      variant="contained"
                      startIcon={<PreviewIcon />}
                      onClick={handlePreview}
                      disabled={!detailedPattern}
                    >
                      Visualiser les itérations
                    </Button>
                  </Box>
                  {showPdfPreview && detailedPattern ? (
                    <Paper variant="outlined" sx={{ p: 1, height: 'calc(100% - 36px)', overflow: 'auto' }}>
                      <PatternPdfViewer
                        key={pdfPreviewKey}
                        latexTemplate={latexTemplate}
                        pythonGenerator={pythonGenerator}
                        isReusable={isReusable}
                        iterations={isReusable ? 3 : 1}
                        patternId={detailedPattern?.id}
                        onRetryWithAiLatex={() => {}}
                        onRetryWithAiPython={() => {}}
                        onPdfGenerated={() => {}}
                      />
                    </Paper>
                  ) : detailedPattern?.preview_pdf_url ? (
                    <Paper variant="outlined" sx={{ p: 1, height: 'calc(100% - 36px)', overflow: 'auto' }}>
                      <PdfDisplay
                        url={detailedPattern.preview_pdf_url}
                        title="Aperçu du pattern"
                      />
                    </Paper>
                  ) : (
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        height: 'calc(100% - 36px)',
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 1,
                        p: 3
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" textAlign="center">
                        Cliquez sur "Visualiser les itérations" pour générer un aperçu PDF
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Python Generator */}
              <Grid item xs={12} md={6}>
                <Box mb={1}>
                  <Typography variant="subtitle2">Fonction Python de génération</Typography>
                </Box>
                <PythonEditor
                  value={pythonGenerator}
                  onChange={() => {}} // Read-only
                  helperText="Lecture seule - Définit une fonction generate() qui retourne un dictionnaire"
                  disabled={true}
                  sx={{
                    '& .MuiInputBase-root': {
                      height: '400px',
                      alignItems: 'flex-start',
                    },
                    '& .MuiInputBase-input': {
                      height: '100% !important',
                      overflow: 'auto !important',
                      maxHeight: '100%',
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      backgroundColor: '#f5f5f5',
                      color: '#666',
                      WebkitTextFillColor: '#666',
                    },
                    '& textarea': {
                      height: '100% !important',
                      overflow: 'auto !important',
                    }
                  }}
                />
              </Grid>

              {/* Generation Results / Solutions */}
              <Grid item xs={12} md={6}>
                <Box mb={1}>
                  <Typography variant="subtitle2">
                    {detailedPattern?.last_test_values && detailedPattern.last_test_values.length > 0
                      ? 'Dernières générations'
                      : 'Informations'}
                  </Typography>
                </Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '400px',
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5'
                  }}
                >
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    <strong>Type:</strong> {isReusable ? 'Réitérable' : 'Statique'}
                  </Typography>

                  {detailedPattern?.last_test_values && detailedPattern.last_test_values.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      {detailedPattern.last_test_values.map((values: any, idx: number) => (
                        <Box key={idx} sx={{ backgroundColor: '#e3f2fd', p: 1, borderRadius: 1, mb: 1 }}>
                          <Typography variant="caption" fontWeight="bold" display="block" mb={0.5}>
                            Génération {idx + 1}:
                          </Typography>
                          <pre style={{ margin: 0, fontSize: '0.75rem', whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                            {JSON.stringify(values, null, 2)}
                          </pre>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Paper>
              </Grid>

              {/* Solutions Section */}
              {detailedPattern?.solutions && detailedPattern.solutions.filter(s => s.is_public).length > 0 && (
                <Grid item xs={12}>
                  <Box mb={1}>
                    <Typography variant="subtitle2">
                      Solutions publiques ({detailedPattern.solutions.filter(s => s.is_public).length})
                    </Typography>
                  </Box>
                  <Grid container spacing={2}>
                    {detailedPattern.solutions.filter(s => s.is_public).map((solution) => (
                      <Grid item xs={12} md={6} key={solution.id}>
                        <Paper sx={{ p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Solution #{solution.id}
                          </Typography>
                          {solution.skills && solution.skills.length > 0 && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                Savoir-faire associés:
                              </Typography>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {solution.skills.map((skill) => (
                                  <Box
                                    key={skill.id}
                                    sx={{
                                      backgroundColor: 'primary.light',
                                      color: 'primary.contrastText',
                                      px: 1,
                                      py: 0.5,
                                      borderRadius: 1,
                                      fontSize: '0.875rem'
                                    }}
                                  >
                                    {skill.name}
                                  </Box>
                                ))}
                              </Box>
                            </Box>
                          )}
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={importing}>
          Fermer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatternViewImportDialog;
